import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { useAuth } from './AuthContext';
import {
  deleteStatsReport,
  fetchStatsContacts,
  fetchStatsReport,
  fetchStatsReports,
  saveStatsReport
} from './api';
import { APP_NAME, BRAND_ASSETS } from './branding';
import { getContestDefinition } from './contests/registry';
import {
  MODE_COLORS,
  buildReportPayload,
  computeClubStats,
  formatReportDateRange,
  suggestReportLabel
} from './statsUtils';
import './StatsPage.css';

const CONTEST_COLORS = ['#558303', '#2563eb', '#c2410c', '#7c3aed', '#0891b2'];
const LIVE_SOURCE = 'live';

function KpiCard({ label, value, note }) {
  return (
    <div className="stats-kpi">
      <span className="stats-kpi-label">{label}</span>
      <span className="stats-kpi-value">{value}</span>
      {note ? <span className="stats-kpi-note">{note}</span> : null}
    </div>
  );
}

function ChartPanel({
  title,
  note,
  span = 6,
  chartHeight = 280,
  footer = null,
  children
}) {
  return (
    <section className={`stats-panel stats-panel-span-${span}`}>
      <h2>{title}</h2>
      {note ? <p className="stats-panel-note">{note}</p> : null}
      <div className="stats-chart-area" style={{ height: chartHeight }}>
        {children}
      </div>
      {footer}
    </section>
  );
}

function ModeTooltip({ active, payload }) {
  if (!active || !payload?.length) {
    return null;
  }

  const item = payload[0]?.payload;
  return (
    <div className="stats-tooltip">
      <strong>{item?.name}</strong>
      <div>{item?.value} QSOs</div>
    </div>
  );
}

function ModeLegend({ rows }) {
  return (
    <div className="stats-mode-legend">
      {rows.map((row) => (
        <span key={row.name}>
          <span className="stats-mode-swatch" style={{ background: row.fill }} />
          {row.name}: {row.value}
        </span>
      ))}
    </div>
  );
}

function StatsReportBody({ stats, contestChartData }) {
  return (
    <div className="stats-grid">
      <ChartPanel
        title="Mode breakdown"
        note="Phone, CW, and Digital"
        span={4}
        chartHeight={220}
        footer={<ModeLegend rows={stats.byMode} />}
      >
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={stats.byMode}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={48}
              outerRadius={78}
              paddingAngle={2}
            >
              {stats.byMode.map((entry) => (
                <Cell key={entry.name} fill={entry.fill} />
              ))}
            </Pie>
            <Tooltip content={<ModeTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </ChartPanel>

      {contestChartData.length > 1 && (
        <ChartPanel title="QSOs by contest" span={4} chartHeight={220}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={contestChartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="value" name="QSOs" radius={[6, 6, 0, 0]}>
                {contestChartData.map((entry) => (
                  <Cell key={entry.slug} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>
      )}

      <ChartPanel
        title="Activity by UTC hour"
        note="When your club was on the air"
        span={contestChartData.length > 1 ? 4 : 8}
        chartHeight={220}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={stats.activityByHour} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="hour" tick={{ fontSize: 11 }} interval={2} />
            <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
            <Tooltip />
            <Bar dataKey="value" name="QSOs" fill="#558303" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartPanel>

      <ChartPanel title="QSOs by band" note="All modes combined" span={6}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={stats.byBand} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
            <Tooltip />
            <Bar dataKey="value" name="QSOs" fill="#2563eb" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartPanel>

      <ChartPanel title="Band × mode" note="Stacked breakdown" span={6}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={stats.bandModeStack} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="band" tick={{ fontSize: 12 }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
            <Tooltip />
            <Legend />
            <Bar dataKey="Phone" stackId="mode" fill={MODE_COLORS.Phone} />
            <Bar dataKey="CW" stackId="mode" fill={MODE_COLORS.CW} />
            <Bar dataKey="Digital" stackId="mode" fill={MODE_COLORS.Digital} />
          </BarChart>
        </ResponsiveContainer>
      </ChartPanel>

      {stats.timelineByMonth.length > 1 && (
        <ChartPanel title="QSO trend by month" note="Monthly activity" span={12} chartHeight={320}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={stats.timelineByMonth} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="statsAreaFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#558303" stopOpacity={0.45} />
                  <stop offset="95%" stopColor="#558303" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Area
                type="monotone"
                dataKey="value"
                name="QSOs"
                stroke="#558303"
                fill="url(#statsAreaFill)"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartPanel>
      )}

      <ChartPanel title="Top operators" note="By QSO count" span={6}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={stats.topOperators}
            layout="vertical"
            margin={{ top: 8, right: 16, left: 8, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
            <YAxis type="category" dataKey="operator" width={72} tick={{ fontSize: 12 }} />
            <Tooltip />
            <Legend />
            <Bar dataKey="phone" name="Phone" stackId="op" fill={MODE_COLORS.Phone} />
            <Bar dataKey="cw" name="CW" stackId="op" fill={MODE_COLORS.CW} />
            <Bar dataKey="digital" name="Digital" stackId="op" fill={MODE_COLORS.Digital} />
          </BarChart>
        </ResponsiveContainer>
      </ChartPanel>

      <ChartPanel title="Top exchanges" note="Sections, counties, or states worked" span={6}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={stats.topExchanges}
            layout="vertical"
            margin={{ top: 8, right: 16, left: 8, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
            <YAxis type="category" dataKey="name" width={48} tick={{ fontSize: 12 }} />
            <Tooltip />
            <Bar dataKey="value" name="QSOs" fill="#7c3aed" radius={[0, 6, 6, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartPanel>

      <section className="stats-panel stats-panel-span-12">
        <h2>Operator summary</h2>
        <p className="stats-panel-note">Full breakdown by logging operator</p>
        <div className="stats-table-wrap">
          <table className="stats-table">
            <thead>
              <tr>
                <th>Operator</th>
                <th>QSOs</th>
                <th>Phone</th>
                <th>CW</th>
                <th>Digital</th>
                <th>Points</th>
                <th>Unique exchanges</th>
              </tr>
            </thead>
            <tbody>
              {stats.byOperator.map((row) => (
                <tr key={row.operator}>
                  <td>{row.operator}</td>
                  <td>{row.contacts}</td>
                  <td>{row.phone}</td>
                  <td>{row.cw}</td>
                  <td>{row.digital}</td>
                  <td>{row.points}</td>
                  <td>{row.uniqueExchanges}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="stats-panel stats-panel-span-12">
        <h2>Band &amp; mode detail</h2>
        <p className="stats-panel-note">Every band/mode combination logged</p>
        <div className="stats-table-wrap">
          <table className="stats-table">
            <thead>
              <tr>
                <th>Band · Mode</th>
                <th>QSOs</th>
              </tr>
            </thead>
            <tbody>
              {stats.byBandMode
                .sort((a, b) => b.value - a.value)
                .map((row) => (
                  <tr key={row.name}>
                    <td>{row.name}</td>
                    <td>{row.value}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function StatsPage() {
  const { clubName, status, adminAccessGranted } = useAuth();
  const activeContestSlug = status.activeContest?.slug || 'field-day';
  const [scope, setScope] = useState('all');
  const [source, setSource] = useState(LIVE_SOURCE);
  const [contacts, setContacts] = useState([]);
  const [savedReports, setSavedReports] = useState([]);
  const [savedReport, setSavedReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveContestSlug, setSaveContestSlug] = useState(activeContestSlug);

  const loadSavedReports = useCallback(async () => {
    const reports = await fetchStatsReports();
    setSavedReports(reports);
    return reports;
  }, []);

  const loadLiveStats = useCallback(async () => {
    const data = await fetchStatsContacts(scope);
    setContacts(data);
    return data;
  }, [scope]);

  const loadData = useCallback(async () => {
    setError('');
    setMessage('');
    try {
      await loadSavedReports();
      if (source === LIVE_SOURCE) {
        setSavedReport(null);
        await loadLiveStats();
      } else {
        const report = await fetchStatsReport(source);
        setSavedReport(report);
      }
    } catch (loadError) {
      setError(loadError.message || 'Unable to load statistics.');
      if (source === LIVE_SOURCE) {
        setContacts([]);
      } else {
        setSavedReport(null);
      }
    } finally {
      setLoading(false);
    }
  }, [source, loadLiveStats, loadSavedReports]);

  useEffect(() => {
    setLoading(true);
    loadData();
  }, [loadData]);

  useEffect(() => {
    setSaveContestSlug(activeContestSlug);
  }, [activeContestSlug]);

  const liveStats = useMemo(() => computeClubStats(contacts), [contacts]);
  const stats = source === LIVE_SOURCE ? liveStats : (savedReport?.statsData || liveStats);

  const contestChartData = useMemo(() => (
    stats.byContest.map((row, index) => ({
      name: getContestDefinition(row.name)?.name || row.name,
      slug: row.name,
      value: row.value,
      fill: CONTEST_COLORS[index % CONTEST_COLORS.length]
    }))
  ), [stats.byContest]);

  const saveCandidates = useMemo(() => {
    const slugs = new Set(
      contacts
        .filter((contact) => contact.deleted !== 'Y')
        .map((contact) => contact.contestSlug || 'field-day')
    );
    if (scope === 'active') {
      slugs.clear();
      slugs.add(activeContestSlug);
    }
    return Array.from(slugs).sort();
  }, [contacts, scope, activeContestSlug]);

  const suggestedLabel = useMemo(() => {
    const slugContacts = contacts.filter(
      (contact) => contact.deleted !== 'Y'
        && (contact.contestSlug || 'field-day') === saveContestSlug
    );
    return suggestReportLabel(saveContestSlug, slugContacts);
  }, [contacts, saveContestSlug]);

  const scopeLabel = source === LIVE_SOURCE
    ? (scope === 'all' ? 'All contests · live logbook' : `${status.activeContest?.name || 'Active contest'} · live logbook`)
    : `${savedReport?.label || 'Saved report'} · archived`;

  const generatedAt = source === LIVE_SOURCE
    ? new Date().toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })
    : (savedReport?.savedAt
      ? new Date(savedReport.savedAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })
      : '');

  const handleSaveReport = async () => {
    const slugContacts = contacts.filter(
      (contact) => contact.deleted !== 'Y'
        && (contact.contestSlug || 'field-day') === saveContestSlug
    );
    const reportStats = computeClubStats(slugContacts);

    if (reportStats.totalContacts === 0) {
      setError(`No contacts found for ${getContestDefinition(saveContestSlug)?.name || saveContestSlug}.`);
      return;
    }

    const payload = buildReportPayload(slugContacts, saveContestSlug, reportStats);
    const existing = savedReports.find(
      (report) => report.year === payload.year && report.contestSlug === payload.contestSlug
    );

    if (existing) {
      const confirmed = window.confirm(
        `Replace saved report "${existing.label}" with fresh statistics from the logbook?`
      );
      if (!confirmed) {
        return;
      }
    }

    setSaving(true);
    setError('');
    setMessage('');
    try {
      const saved = await saveStatsReport(payload);
      await loadSavedReports();
      setSource(saved.id);
      setMessage(`Saved "${saved.label}" (${saved.contactCount} QSOs).`);
    } catch (saveError) {
      setError(saveError.message || 'Unable to save report.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteReport = async () => {
    if (source === LIVE_SOURCE || !savedReport) {
      return;
    }

    const confirmed = window.confirm(`Delete saved report "${savedReport.label}"? This cannot be undone.`);
    if (!confirmed) {
      return;
    }

    setError('');
    setMessage('');
    try {
      await deleteStatsReport(source);
      setSource(LIVE_SOURCE);
      setSavedReport(null);
      await loadSavedReports();
      await loadLiveStats();
      setMessage('Saved report deleted.');
    } catch (deleteError) {
      setError(deleteError.message || 'Unable to delete report.');
    }
  };

  if (loading) {
    return (
      <div className="stats-page">
        <div className="stats-loading">Loading club statistics...</div>
      </div>
    );
  }

  return (
    <div className="stats-page">
      <div className="stats-toolbar">
        <div className="stats-toolbar-left">
          <Link to="/" className="stats-nav-link">← Logbook</Link>
          <Link to="/display" className="stats-nav-link">Scoreboard</Link>
        </div>
        <div className="stats-toolbar-right">
          <select
            className="stats-scope-select"
            value={source}
            onChange={(event) => setSource(event.target.value)}
            aria-label="Report source"
          >
            <option value={LIVE_SOURCE}>Live logbook data</option>
            {savedReports.map((report) => (
              <option key={report.id} value={report.id}>
                {report.label} ({report.contactCount} QSOs)
              </option>
            ))}
          </select>
          {source === LIVE_SOURCE && (
            <select
              className="stats-scope-select"
              value={scope}
              onChange={(event) => setScope(event.target.value)}
              aria-label="Statistics scope"
            >
              <option value="all">All contests</option>
              <option value="active">Active contest only</option>
            </select>
          )}
          <button type="button" className="stats-btn stats-btn-secondary" onClick={() => {
            setLoading(true);
            loadData();
          }}
          >
            Refresh
          </button>
          {adminAccessGranted && source === LIVE_SOURCE && (
            <>
              {saveCandidates.length > 1 && (
                <select
                  className="stats-scope-select"
                  value={saveContestSlug}
                  onChange={(event) => setSaveContestSlug(event.target.value)}
                  aria-label="Contest to save"
                >
                  {saveCandidates.map((slug) => (
                    <option key={slug} value={slug}>
                      Save {getContestDefinition(slug)?.name || slug}
                    </option>
                  ))}
                </select>
              )}
              <button
                type="button"
                className="stats-btn"
                onClick={handleSaveReport}
                disabled={saving}
                title={`Save as ${suggestedLabel}`}
              >
                {saving ? 'Saving…' : `Save ${suggestedLabel}`}
              </button>
            </>
          )}
          {adminAccessGranted && source !== LIVE_SOURCE && (
            <button type="button" className="stats-btn stats-btn-danger" onClick={handleDeleteReport}>
              Delete saved report
            </button>
          )}
          <button type="button" className="stats-btn stats-btn-secondary" onClick={() => window.print()}>
            Print report
          </button>
        </div>
      </div>

      {error && <div className="stats-banner stats-banner-error">{error}</div>}
      {message && <div className="stats-banner stats-banner-success">{message}</div>}

      <div className="stats-shell">
        <header className="stats-hero">
          <div className="stats-hero-top">
            <div className="stats-hero-brand">
              <img src={BRAND_ASSETS.icon} alt="" className="stats-hero-logo" aria-hidden="true" />
              <div>
                <h1>{clubName || APP_NAME} — Activity Report</h1>
                <p className="stats-hero-subtitle">{scopeLabel}</p>
              </div>
            </div>
            <div className="stats-hero-meta">
              <strong>{formatReportDateRange(stats.dateRange)}</strong>
              <span>
                {source === LIVE_SOURCE ? `Generated ${generatedAt}` : `Saved ${generatedAt}`}
              </span>
              {source !== LIVE_SOURCE && savedReport?.savedBy && (
                <span>Saved by {savedReport.savedBy}</span>
              )}
            </div>
          </div>

          <div className="stats-kpi-grid">
            <KpiCard label="Total QSOs" value={stats.totalContacts.toLocaleString()} />
            <KpiCard label="Unique calls" value={stats.uniqueCallsigns.toLocaleString()} />
            <KpiCard label="Operators" value={stats.operatorCount.toLocaleString()} />
            <KpiCard label="Exchanges worked" value={stats.uniqueExchanges.toLocaleString()} />
            <KpiCard
              label="Field Day points"
              value={stats.qsoPoints.toLocaleString()}
              note="Phone ×1, CW/Digital ×2"
            />
            <KpiCard label="Contests" value={stats.byContest.length.toLocaleString()} />
          </div>
        </header>

        {stats.totalContacts === 0 ? (
          <div className="stats-panel stats-panel-span-12">
            <h2>No contacts yet</h2>
            <p className="stats-panel-note">
              Log QSOs in the logbook to populate this report, or choose a saved year report from the dropdown.
            </p>
          </div>
        ) : (
          <StatsReportBody stats={stats} contestChartData={contestChartData} />
        )}
      </div>

      <footer className="stats-footer">
        {APP_NAME} · Club activity report · {clubName || APP_NAME}
      </footer>
    </div>
  );
}

export default StatsPage;
